import { Colors, Icon, Pagination } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import EmployeeWithoutObjectiveTile from './EmployeeWithoutObjectiveTile';
import {
    ObjectiveLoadErrorMessage,
    ObjectiveSectionLoader,
} from '../components/ObjectivePageStates';
import { IconCircle } from '../components/surfaces';

import type { EmployeeWithoutObjective } from '@/types/objectives';

import { EMPTY_STATE_TEXT, OWNER_LIST_PANEL } from '@/components/objectives/objectiveStyles';
import { EMPLOYEES_WITHOUT_OBJECTIVES_PAGE_SIZE } from '@/types/objectives';

interface EmployeesWithoutObjectivesListProps {
    employees: EmployeeWithoutObjective[];
    /** Served count for the current query, not the page's own length: it drives the pager. */
    totalCount: number;
    pageNumber: number;
    onPageChange: (pageNumber: number) => void;
    onSelectEmployee: (employee: EmployeeWithoutObjective) => void;
    isLoading: boolean;
    hasError: boolean;
    /** The reader picked custom dates but only one bound: there is no window to ask about yet,
     * and an empty grid would read as "everybody has an objective". */
    hasIncompleteRange: boolean;
    /** Whether a search term or a company-attribute filter is narrowing the list: an empty
     * result then means "nothing matches", not "everybody has an objective". */
    isSearchActive: boolean;
    isFilterActive: boolean;
}

/** The white panel holding the employee grid, with its loading, error and empty states. */
export default function EmployeesWithoutObjectivesList({
    employees,
    totalCount,
    pageNumber,
    onPageChange,
    onSelectEmployee,
    isLoading,
    hasError,
    hasIncompleteRange,
    isSearchActive,
    isFilterActive,
}: EmployeesWithoutObjectivesListProps) {
    const { t } = useTranslation();
    const totalPages = Math.max(1, Math.ceil(totalCount / EMPLOYEES_WITHOUT_OBJECTIVES_PAGE_SIZE));

    const renderEmptyState = () => {
        const narrowedMessageKey = isSearchActive
            ? 'employeesWithoutObjectives.empty.noSearchResults'
            : 'employeesWithoutObjectives.empty.noFilterResults';

        return (
            <div
                className={'flex flex-col items-center gap-4 py-12 text-center'}
                data-testid={'employees-without-objectives-empty'}
            >
                <IconCircle className={'bg-obj-tint h-[64px] w-[64px]'}>
                    <Icon
                        name={'users-profile-2user'}
                        size={32}
                        color={Colors.STEP_RAIL_TURQUOISE}
                        strokeWidth={1.6}
                    />
                </IconCircle>

                {isSearchActive || isFilterActive ? (
                    <p className={EMPTY_STATE_TEXT}>{t(narrowedMessageKey)}</p>
                ) : (
                    <div className={'flex flex-col gap-1'}>
                        <p className={'text-navy font-medium'}>
                            {t('employeesWithoutObjectives.empty.title')}
                        </p>

                        <p className={'text-obj-slate text-[14px]'}>
                            {t('employeesWithoutObjectives.empty.description')}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const renderContent = () => {
        if (isLoading) {
            return <ObjectiveSectionLoader dataTestId={'employees-without-objectives-loader'} />;
        }

        if (hasError) {
            return (
                <ObjectiveLoadErrorMessage
                    message={t('employeesWithoutObjectives.errors.loading')}
                    dataTestId={'employees-without-objectives-error'}
                />
            );
        }

        if (hasIncompleteRange) {
            return (
                <p
                    className={`${EMPTY_STATE_TEXT} py-12 text-center`}
                    data-testid={'employees-without-objectives-incomplete-range'}
                >
                    {t('employeesWithoutObjectives.incompleteRange')}
                </p>
            );
        }

        if (employees.length === 0) {
            return renderEmptyState();
        }

        return (
            <>
                <div className={'responsive-grid-1-2-3 gap-4'}>
                    {employees.map((employee) => (
                        <EmployeeWithoutObjectiveTile
                            key={employee.id}
                            employee={employee}
                            onSelect={() => onSelectEmployee(employee)}
                        />
                    ))}
                </div>

                <Pagination
                    currentPage={pageNumber}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    pageSize={EMPLOYEES_WITHOUT_OBJECTIVES_PAGE_SIZE}
                    onPageChange={onPageChange}
                    isLoading={isLoading}
                    variant={'rounded'}
                    dataTestId={'employees-without-objectives-pagination'}
                />
            </>
        );
    };

    return (
        <div
            className={`${OWNER_LIST_PANEL} flex flex-col gap-6`}
            data-testid={'employees-without-objectives-list'}
        >
            {renderContent()}
        </div>
    );
}
